import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

const ENTER_KEY = 13
const PRODUCT_PATH = "/admin/popular_redemptions/product/"
// Bootstrap announces show.bs.modal through jQuery's trigger, which dispatches no DOM event, and
// daterangepicker hands a picked range to an option callback rather than to an event at all.
const JQUERY_NAMESPACE = ".adminPopularRedemptions"

// One controller, two reports: the ranked report renders the modal and no picker, the inventory
// velocity report the picker and no modal. Each half is guarded on its own target, which is what
// jQuery's no-op over an empty set did.
export default class extends Controller {
  static get targets() { return ["dateRange", "modal"]; }
  static get values() { return { firstRedemptionDate: String, url: String }; }

  connect() {
    this.startPicker()
    this.watchModal()
  }

  disconnect() {
    if (this.hasDateRangeTarget) {
      const picker = $(this.dateRangeTarget).data("daterangepicker")
      if (picker) { picker.remove(); }
    }

    if (this.hasModalTarget) { $(this.modalTarget).off(JQUERY_NAMESPACE); }
  }

  startPicker() {
    if (!this.hasDateRangeTarget) { return; }

    $(this.dateRangeTarget).daterangepicker({
      opens: "right",
      alwaysShowCalendars: true,
      showCustomRangeLabel: false,
      ranges: {
        "Today": [moment(), moment()],
        "Yesterday": [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
        "All Time": [moment(this.firstRedemptionDateValue), moment()]
      }
    }, (start, end) => this.applyRange(start, end))
  }

  // The url carries whatever filters the server rendered, so a search survives a range change.
  applyRange(start, end) {
    const url = new URL(this.urlValue)
    url.searchParams.append("filter[start_date]", start.format("MMDDYYYY"))
    url.searchParams.append("filter[end_date]", end.format("MMDDYYYY"))

    // `urlValue` is a whole URL lifted from a data attribute (an `_url` route helper, not a
    // `_path` helper - it carries its own origin), the same shape as the guarded
    // `subdomain_collections` `data-href` case, so it is asserted before navigating per the S3
    // rule rather than trusting the markup outright. The failing branch does not navigate.
    if (RequestHelper.isSameOriginUrl(url.href)) {
      window.location.replace(url.href)
    } else {
      this.logFailedCall(`Refused to navigate to a cross-origin filter URL: ${url.href}`)
    }
  }

  watchModal() {
    if (!this.hasModalTarget) { return; }

    $(this.modalTarget).on(`show.bs.modal${JQUERY_NAMESPACE}`, (event) => {
      this.retrieveVariant(`${PRODUCT_PATH}${event.relatedTarget.dataset.productId}`)
    })
  }

  changeProductImage(event) {
    event.preventDefault()

    this.retrieveVariant(event.currentTarget.dataset.path)
  }

  // What lt-enter did for the compiled fragment: jQuery splits its "keydown, keypress" binding on
  // whitespace, so only keypress was ever a real event type.
  changeProductImageOnEnter(event) {
    if (event.which !== ENTER_KEY) { return; }

    this.changeProductImage(event)
  }

  // Stimulus reconnects the chevrons in the swapped-in fragment on its own, which is what used to
  // was doing here.
  retrieveVariant(url) {
    fetch(url, RequestHelper.defaultRequestOptions()).
      then(response => {
        if (!response.ok) { throw new Error(`Product lookup failed (${response.status})`); }
        return response.text()
      }).
      then(html => { this.modalTarget.querySelector(".modal-dialog .modal-content").outerHTML = html }).
      catch(this.logFailedCall)
  }

  logFailedCall(response) {
    console.warn(response)
  }
}
